/**
 * 위험성평가 상세 Raw data 엑셀 다운로드
 *
 * 대상 게시판은 813(위험성평가(직영)), 814(위험성평가(수급업체))와
 * 815(위험성평가 실시현황, 813/814 통합 목록)다.
 * 버튼 노출 조건은 canDownloadRiskRawExcel 하나로 관리한다.
 *
 * 기존 "List Excel 다운로드"는 문서 1건당 1행이라 위험성평가표 내부(원인/위험요인/감소대책 등)를
 * 볼 수 없다. 여기서는 위험성평가표의 한 줄을 엑셀 한 행으로 펼치고, 문서 단위 정보(사업장/팀/분류/
 * 작성자/정기·수시/작업빈도)는 각 행에 반복해서 채운다.
 * (2025~2026 당진공장 Raw data 를 클라이언트에 제공하기 위해 만들었고, 재사용 요청이 있어 남겨둔다)
 *
 * 조회 범위는 화면 목록과 동일하다. 목록에 보이는 문서만 대상이며, 검색 조건도 화면에 입력된 것을
 * 그대로 사용한다. 다만 결재 승인 완료건만 내보낸다.
 */

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// 813 -> 736(공정분류표(직영), 팀명), 814 -> 895(공정분류표(수급업체), 업체명)
const TEAM_KEY_BY_BOARD = { 813: "팀명", 814: "업체명" };

// Raw data 다운로드를 허용할 소속 업체. 클라이언트 내부 직원들이다.
const ALLOWED_COMPANY_IDS = [914, 915, 916, 917];

/**
 * Raw data 엑셀 다운로드 버튼 노출 여부.
 *
 * 문서 본문 전체를 한 번에 긁어오는 무거운 기능이라 대상을 제한한다.
 * 시스템관리자이거나, 클라이언트 내부 직원(ALLOWED_COMPANY_IDS 소속)이면 사용할 수 있다.
 *
 * @param {object} store vuex store
 */
export function canDownloadRiskRawExcel(store) {
  const userInfo = (store && store.state && store.state.auth && store.state.auth.userInfo) || {};
  if (parseInt(userInfo.is_superuser) > 0) return true;
  return ALLOWED_COMPANY_IDS.includes(Number(userInfo.company_id));
}

const COLUMNS = [
  { header: "No.", key: "no", width: 7 },
  { header: "문서ID", key: "postId", width: 10 },
  { header: "구분", key: "boardLabel", width: 10 },
  { header: "평가연도", key: "year", width: 9 },
  { header: "평가일", key: "date", width: 12 },
  { header: "사업장", key: "field", width: 14 },
  { header: "팀(업체)명", key: "team", width: 16 },
  { header: "공장명", key: "factory", width: 16 },
  { header: "대분류", key: "large", width: 16 },
  { header: "중분류", key: "medium", width: 16 },
  { header: "소분류", key: "small", width: 20 },
  { header: "작성자", key: "writer", width: 10 },
  { header: "정기/수시", key: "cycle", width: 10 },
  { header: "수시사유", key: "cycleReason", width: 16 },
  { header: "작업빈도(일)", key: "freqDay", width: 11 },
  { header: "작업빈도(주)", key: "freqWeek", width: 11 },
  { header: "작업빈도(월)", key: "freqMonth", width: 11 },
  { header: "작업빈도(분기)", key: "freqQuarter", width: 12 },
  { header: "작업빈도(년)", key: "freqYear", width: 11 },
  { header: "1회 작업시간(분)", key: "freqMinutes", width: 14 },
  { header: "평가유형", key: "assessType", width: 10 },
  { header: "행번호", key: "rowNo", width: 7 },
  { header: "분류", key: "category", width: 16 },
  { header: "원인", key: "cause", width: 20, wrap: true },
  { header: "작업단계", key: "step", width: 20, wrap: true },
  { header: "위험요인", key: "hazard", width: 40, wrap: true },
  { header: "현재조치 관리상태", key: "currentControl", width: 40, wrap: true },
  { header: "발생빈도", key: "frequency", width: 9 },
  { header: "위험강도", key: "severity", width: 9 },
  { header: "단위위험", key: "unitRisk", width: 9 },
  { header: "위험도", key: "riskScore", width: 9 },
  { header: "중요위험", key: "importantRisk", width: 9 },
  { header: "감소대책", key: "countermeasure", width: 50, wrap: true },
];

function text(value) {
  if (value === null || value === undefined) return "";
  return `${value}`;
}

/**
 * 평가일자는 화면/문서에 따라 "2025-03-05", "2025-3-5", "2025-03-05T00:00:00.000Z" 등으로 섞여 저장돼 있다.
 * 엑셀에서 정렬/필터가 되도록 yyyy-MM-dd 로 통일한다.
 */
function normalizeDate(value) {
  const str = text(value).trim();
  const matched = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (!matched) return str;
  return `${matched[1]}-${matched[2].padStart(2, "0")}-${matched[3].padStart(2, "0")}`;
}

/** "5" 처럼 문자열로 저장된 값이 많아, 숫자로 읽히는 것은 숫자로 넣어 엑셀에서 집계할 수 있게 한다. */
function numeric(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isNaN(num) ? `${value}` : num;
}

/**
 * 팀(업체)명/공장명/대·중·소분류는 문서에 저장된 값이 작성 시점의 공정분류표 스냅샷(body.공정분류.raw)이라
 * 공정분류표가 개정(리네임)되면 옛 이름으로 남는다. 목록/상세 화면과 동일하게 최신값을 우선 사용하고,
 * 최신값 조회에 실패했을 때에만 저장된 raw 값으로 폴백한다.
 *
 * 최신값은 목록마다 다른 이름으로 붙는다.
 * 813 목록은 latest736, 814 목록은 latest895, 815(통합 목록)는 latestClassification.
 */
function latestClassificationOf(item) {
  return (item && (item.latest736 || item.latest895 || item.latestClassification)) || null;
}

function classificationValue(item, body, key) {
  const latest = latestClassificationOf(item);
  if (latest && latest[key]) return latest[key];
  const raw = body && body.공정분류 && body.공정분류.raw;
  return (raw && raw[key]) || "";
}

function teamName(item, body, boardId) {
  const latest = latestClassificationOf(item);
  const key = TEAM_KEY_BY_BOARD[boardId] || "팀명";
  if (latest && latest[key]) return latest[key];
  // 공정분류표 최신값이 없으면 문서에 입력된 팀(업체)명을 쓴다
  return (body && body.위험성평가정보 && body.위험성평가정보.팀) || "";
}

/** 정기/수시 + 수시일 때의 사유(기타는 직접입력값) */
function cycleOf(body) {
  const 공정분류 = (body && body.공정분류) || {};
  const 구분 = text(공정분류.구분);
  if (구분 !== "수시") return { cycle: 구분, cycleReason: "" };

  const 수시 = text(공정분류.구분_수시);
  const reason = 수시 === "기타(직접입력)" ? text(공정분류.구분_수시_기타) || 수시 : 수시;
  return { cycle: 구분, cycleReason: reason };
}

/**
 * 위험성평가표에서 (평가유형, 행배열) 쌍을 뽑는다.
 * 위험성평가구분이 "DKRAS+JSA" 면 두 표가 모두 존재하므로 둘 다 펼친다.
 * 아주 오래된 문서는 위험성평가표가 배열 하나로 저장돼 있을 수 있어 그 형태도 받아준다.
 */
function tablesOf(body) {
  const 표 = (body && body.위험성평가표) || {};
  // 중요위험은 표가 DKRAS/JSA 로 나뉘기 전에는 위험성평가표 바로 아래에 있었다. 목록 화면도 이 값을 쓴다.
  const 기본중요위험 = text(표.중요위험);
  // 구버전 문서는 표에 유형 정보가 없고, 문서 단위의 위험성평가구분이 유형을 알려준다.
  const 기본유형 = text(body && body.위험성평가구분);

  if (Array.isArray(표)) return [{ type: 기본유형, rows: 표, importantRisk: 기본중요위험 }];

  const tables = [];
  for (const type of ["DKRAS", "JSA"]) {
    const table = 표[type];
    if (table && Array.isArray(table.data) && table.data.length > 0) {
      tables.push({ type, rows: table.data, importantRisk: text(table.중요위험) || 기본중요위험 });
    }
  }
  if (tables.length > 0) return tables;

  // 구버전 문서: DKRAS/JSA 로 나뉘기 전이라 위험성평가표 바로 아래에 data 가 들어 있다.
  // (행 내부 키는 지금과 동일하다. 예: 150723 번 문서)
  if (Array.isArray(표.data) && 표.data.length > 0) {
    return [{ type: 기본유형, rows: 표.data, importantRisk: 기본중요위험 }];
  }

  return [];
}

/**
 * 결재 승인 완료건만 내보낸다.
 * 813/814 목록은 "진행중 + 완료"를 함께 보여주므로 여기서 승인 완료건만 남긴다.
 * 결재 상태 정보가 아예 없는 문서는 대상에서 빼지 않는다.
 */
function isApproved(item) {
  const status = item && item.document_status;
  if (!status || !status.code) return true;
  return status.code === "approved";
}

/** 문서 1건을 위험성평가표 행 단위로 펼친다. */
function flattenPost(item, boardId) {
  let content = null;
  try {
    content = JSON.parse(item.post_content);
  } catch (e) {
    return [];
  }

  const body = (content && content.body) || {};
  const stdInfo = body.stdInfo || {};
  const 평가일 = normalizeDate(body.위험성평가정보 && body.위험성평가정보.평가일자);
  const 작업빈도 = body.작업빈도 || {};
  const { cycle, cycleReason } = cycleOf(body);

  const base = {
    postId: item.post_id || item.id,
    boardLabel: boardId === 814 ? "수급업체" : "직영",
    year: 평가일 ? 평가일.slice(0, 4) : "",
    date: 평가일,
    field: text(stdInfo.field_name),
    team: teamName(item, body, boardId),
    factory: classificationValue(item, body, "공장명"),
    large: classificationValue(item, body, "대분류"),
    medium: classificationValue(item, body, "중분류"),
    small: classificationValue(item, body, "소분류"),
    writer: text(stdInfo.writer_name),
    cycle,
    cycleReason,
    freqDay: numeric(작업빈도.일),
    freqWeek: numeric(작업빈도.주),
    freqMonth: numeric(작업빈도.월),
    freqQuarter: numeric(작업빈도.분기),
    freqYear: numeric(작업빈도.년),
    freqMinutes: numeric(작업빈도.회당작업시간),
  };

  const rows = [];
  for (const table of tablesOf(body)) {
    table.rows.forEach((row, index) => {
      rows.push({
        ...base,
        assessType: table.type,
        rowNo: index + 1,
        // JSA 표는 분류/원인 대신 작업단계를 쓰고, 현재관리상태의 필드명도 다르다.
        category: text(row.분류),
        cause: text(row.원인),
        step: text(row.작업단계),
        hazard: text(row.위험요인),
        currentControl: text(row.현재조치관리상태 || row.현재관리상태),
        frequency: numeric(row.발생빈도),
        severity: numeric(row.위험강도),
        unitRisk: text(row.단위위험),
        riskScore: numeric(row.위험도),
        importantRisk: table.importantRisk,
        countermeasure: text(row.감소대책),
      });
    });
  }

  // 위험성평가표가 비어 있는 문서도 누락되지 않도록 문서 정보만 담은 행을 한 줄 남긴다.
  if (rows.length === 0) {
    rows.push({ ...base, assessType: "", rowNo: "" });
  }

  return rows;
}

/**
 * 목록 조회 결과(post 배열)를 위험성평가표 행 단위로 펼쳐 xlsx 로 저장한다.
 *
 * @param {Array} items  post 목록. post_content(문서 원본 JSON)와, 가능하면 최신 공정분류표
 *                       (813 은 latest736, 814 는 latest895)가 붙어 있어야 한다.
 * @param {string} baseName 파일명 앞부분. 뒤에 _rawdata_{타임스탬프}.xlsx 가 붙는다.
 * @param {number} targetBoardId 813(직영) 또는 814(수급업체). 팀/업체명 필드명이 갈리므로 필요하다.
 * @returns {Promise<number>} 생성된 데이터 행 수
 */
export async function downloadRiskRawExcel(items, baseName, targetBoardId) {
  const rows = [];
  for (const item of items || []) {
    if (!isApproved(item)) continue;
    // 목록 응답에 board_id 가 없을 수 있어, 호출한 화면의 게시판 id 를 기본값으로 쓴다.
    const boardId = Number(item.board_id) || Number(targetBoardId);
    rows.push(...flattenPost(item, boardId));
  }

  rows.forEach((row, index) => {
    row.no = index + 1;
  });

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("위험성평가 Raw data");
  sheet.columns = COLUMNS;
  sheet.addRows(rows);

  // 위험요인/감소대책 등은 여러 줄로 입력되는 경우가 많다.
  // 자동 줄 바꿈을 켜지 않으면 셀에 값이 다 들어 있어도 엑셀 화면에는 첫 줄만 보이므로,
  // 내용이 누락된 것처럼 오해하게 된다. 긴 텍스트 컬럼은 줄 바꿈을 켜 둔다.
  COLUMNS.forEach((column) => {
    if (!column.wrap) return;
    sheet.getColumn(column.key).alignment = { wrapText: true, vertical: "top" };
  });

  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: COLUMNS.length } };

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `${baseName}_rawdata_${new Date().format("yyyyMMdd_HHmmss")}.xlsx`;
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);

  return rows.length;
}
